import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Sorter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { CartReservedSeat } from '../SeatingPlan.js';
import { CartItem } from './CartItem.js';
export class Ticket extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.scannedAt = null;
        this.createdAt = new Date();
        this.updatedAt = new Date();
        this.seat = null;
        this.originalSeat = null;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Ticket.prototype, "id", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Ticket.prototype, "scannedAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Ticket.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Ticket.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Ticket.prototype, "secret", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Ticket.prototype, "index", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Ticket.prototype, "total", void 0);
__decorate([
    field({ decoder: CartReservedSeat, nullable: true, version: 216 })
], Ticket.prototype, "seat", void 0);
__decorate([
    field({ decoder: CartReservedSeat, nullable: true, version: 216 })
], Ticket.prototype, "originalSeat", void 0);
/**
 * Ticket extended with the order data. This is
 * required because the user doesn't have access
 * to the order. So we need to add the relevant data
 * */
export class TicketPublic extends Ticket {
    constructor() {
        super(...arguments);
        this.items = [];
    }
    get isSingle() {
        return this.items.length === 1 && this.items[0].product.isTicket;
    }
    getPrice(order) {
        var _a, _b, _c;
        if (!this.isSingle) {
            if (order) {
                return order.data.totalPrice;
            }
            return Math.max(0, this.items.reduce((c, item) => { var _a; return c + ((_a = item.price) !== null && _a !== void 0 ? _a : 0); }, 0));
        }
        const item = this.items[0];
        return ((_a = item.unitPrice) !== null && _a !== void 0 ? _a : 0) + ((_c = (_b = this.seat) === null || _b === void 0 ? void 0 : _b.price) !== null && _c !== void 0 ? _c : 0);
    }
    getTitle() {
        if (!this.isSingle) {
            return $t(`de971042-551d-43d2-ab47-e76132156887`);
        }
        return this.items[0].product.name;
    }
    getChangedSeatString(webshop, isCustomer) {
        if (!this.isSingle) {
            return null;
        }
        const item = this.items[0];
        if (!item || !item.product.seatingPlanId) {
            return null;
        }
        if (!this.originalSeat) {
            return null;
        }
        if (!this.seat) {
            return null;
        }
        if (this.seat.equals(this.originalSeat)) {
            return null;
        }
        const to = this.seat.getNameString(webshop, item.product);
        const from = this.originalSeat.getNameString(webshop, item.product);
        if (isCustomer) {
            return $t(`bfbfa403-7d12-4cbd-b299-2dd3a8b32ad1`, { from, to });
        }
        return $t(`6d281e06-4827-4b95-9434-9e3d5d96215a`, { from, to });
    }
    getIndexDescriptionString(webshop) {
        if (!this.isSingle) {
            return '';
        }
        const description = this.getIndexDescription(webshop);
        return description.map(d => d.title + ': ' + d.value).join('\n');
    }
    getIndexDescription(webshop) {
        if (!this.isSingle) {
            return [];
        }
        const item = this.items[0];
        if (!item || !item.product.seatingPlanId) {
            return [];
        }
        const seat = this.seat;
        if (seat) {
            const r = seat.getName(webshop, item.product);
            if (r.length > 0) {
                return r;
            }
        }
        return [
            {
                title: $t(`8435edb8-619e-4ddf-b5c8-4d1a2bc44acc`),
                value: $t(`896f4cd8-ee2e-4ff6-a174-a1c8078b6a52`),
            },
        ];
    }
    getSeatingPlanId() {
        if (!this.isSingle) {
            return null;
        }
        return this.items[0].product.seatingPlanId;
    }
    getIndexText() {
        if (this.items.length !== 1) {
            if (this.total > 1) {
                return this.index + ' / ' + this.total;
            }
            return null;
        }
        const item = this.items[0];
        const nameField = item.fieldAnswers.findIndex(a => a.field.name.toLowerCase().includes('naam'));
        if (nameField !== -1) {
            return item.fieldAnswers[nameField].answer;
        }
        if (this.total > 1) {
            return this.index + ' / ' + this.total;
        }
        return null;
    }
    static sort(a, b) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        return Sorter.stack(Sorter.byNumberValue(a.items.length, b.items.length), Sorter.byStringValue((_c = (_b = (_a = a.items[0]) === null || _a === void 0 ? void 0 : _a.product) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '', (_f = (_e = (_d = b.items[0]) === null || _d === void 0 ? void 0 : _d.product) === null || _e === void 0 ? void 0 : _e.name) !== null && _f !== void 0 ? _f : ''), Sorter.byStringValue((_h = (_g = a.items[0]) === null || _g === void 0 ? void 0 : _g.id) !== null && _h !== void 0 ? _h : '', (_k = (_j = b.items[0]) === null || _j === void 0 ? void 0 : _j.id) !== null && _k !== void 0 ? _k : ''), // group same options and items
        -1 * Sorter.byNumberValue(a.index, b.index));
    }
    get isValid() {
        return this.items.length > 0;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(CartItem) })
], TicketPublic.prototype, "items", void 0);
/**
 * Structure if you do have access to the order (needs proof first: be an admin or pass the order id along the request)
 */
export class TicketOrder extends Ticket {
    constructor() {
        super(...arguments);
        /**
         * itemId is private because access to the order is needed to be able to look it up.
         */
        this.itemId = null;
    }
    getPublic(order) {
        if (this.itemId) {
            const item = order.data.cart.items.find(i => i.id === this.itemId);
            return TicketPublic.create({
                ...this,
                items: item ? [item] : [],
            });
        }
        else {
            return TicketPublic.create({
                ...this,
                items: order.data.cart.items,
            });
        }
    }
}
__decorate([
    field({ decoder: StringDecoder })
], TicketOrder.prototype, "orderId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], TicketOrder.prototype, "itemId", void 0);
export class TicketPublicPrivate extends TicketPublic {
    constructor() {
        super(...arguments);
        /**
         * Private information
         */
        this.scannedBy = null;
        this.deletedAt = null;
    }
    getPublic() {
        return this;
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], TicketPublicPrivate.prototype, "scannedBy", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 229 })
], TicketPublicPrivate.prototype, "deletedAt", void 0);
/**
 * Structure if you do have access to the order (needs proof first: be an admin or pass the order id along the request)
 */
export class TicketPrivate extends TicketOrder {
    constructor() {
        super(...arguments);
        /**
         * Private information
         */
        this.scannedBy = null;
        this.deletedAt = null;
    }
    getPublic(order) {
        return TicketPublicPrivate.create({
            ...super.getPublic(order),
            scannedBy: this.scannedBy,
            deletedAt: this.deletedAt,
        });
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], TicketPrivate.prototype, "scannedBy", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 229 })
], TicketPrivate.prototype, "deletedAt", void 0);
//# sourceMappingURL=Ticket.js.map